// src/data/servicesData.ts

export interface DescriptionContent {
  [key: string]: string | string[];
}

export interface SubCategory {
  id: string;
  nameKey: string;
  imageUrl?: string;
}

export interface Category {
  id: string;
  nameKey: string;
  svgPlaceholder?: string;
  imageUrl?: string;
  subCategories: SubCategory[];
}

export interface Service {
  id: string;
  titleKey: string;
  descriptionKeys: DescriptionContent;
  // image can be optional for services like video where we don't want an image
  imageUrl?: string;
  // Desktop uses `serviceImage` and `petImage` in some sections; keep optional for mobile/desktop
  serviceImage?: string;
  petImage?: string;
  iconKey?: string;
  categories?: Category[];
  // optional flag to indicate availability for UI (coming soon)
  enabled?: boolean;
  // optional short label to use in condensed nav (MiniNavbar)
  navTitle?: string;
  // optional video ID for modal playback
  videoId?: string;
}

export const servicesData: Service[] = [
  {
    id: "ai-assistance",
    titleKey: "services.ai.title",
    iconKey: "brain",
    descriptionKeys: {
      intro: "services.ai.description.intro",
      tagline: "services.ai.description.tagline",
      benefit: "services.ai.description.benefit",
      listHeader: "services.ai.description.listHeader",
      listItems: [
        "services.ai.description.listItem1",
        "services.ai.description.listItem2",
        "services.ai.description.listItem3",
      ],
      benefitExtended: "services.ai.description.benefitExtended",
      outro: "services.ai.description.outro",
      planNote: "services.ai.description.planNote",
      sourcesShort: "services.ai.description.sourcesShort",
    },
    imageUrl: "/images/services/NALAAI.webp", // updated to user-provided screenshot
    videoId: "TMry8zNtGLA",
    serviceImage: "/images/services/NALAAI.webp",
    petImage: "/images/logo/NALAaiPet(1).webp",
  },
  {
    id: "soapvett",
    titleKey: "services.soapvett.title",
    iconKey: "microphone",
    descriptionKeys: {
      intro: "services.soapvett.description.intro",
      tagline: "services.soapvett.description.tagline",
      benefit: "services.soapvett.description.benefit",
      listHeader: "services.soapvett.description.listHeader",
      listItems: [
        "services.soapvett.description.listItem1",
        "services.soapvett.description.listItem2",
        "services.soapvett.description.listItem3",
        "services.soapvett.description.listItem4",
        "services.soapvett.description.listItem5",
        "services.soapvett.description.listItem6",
      ],
      outro: "services.soapvett.description.outro",
      planNote: "services.soapvett.description.planNote",
    },
    imageUrl: "/images/services/SOAPvett.webp",
    videoId: "6pfV2TtsBko",
    serviceImage: "/images/services/SOAPvett.webp",
    petImage: "/images/logo/SOAPvettPet(1).webp",
  },
  {
    id: "clinicflow",
    titleKey: "services.clinicflow.title",
    iconKey: "analytics",
    descriptionKeys: {
      intro: "services.clinicflow.description.intro",
      tagline: "services.clinicflow.description.tagline",

      listHeader: "services.clinicflow.description.listHeader",
      listItems: [
        "services.clinicflow.description.listItem1",
        "services.clinicflow.description.listItem2",
        "services.clinicflow.description.listItem3",
        "services.clinicflow.description.listItem4",
        "services.clinicflow.description.listItem5",
      ],
      outro: "services.clinicflow.description.outro",
    },
    imageUrl: "/images/services/ClinicFlow.webp",
    videoId: "nwjSrRDizi4",
    serviceImage: "/images/services/ClinicFlow.webp",
    petImage: "/images/logo/ClinicFlowPet.webp",
  },
  {
    id: "templates",
    titleKey: "services.templates.title",
    iconKey: "template",
    descriptionKeys: {
      intro: "services.templates.description.intro",
      tagline: "services.templates.description.tagline",
      benefit: "services.templates.description.benefit",
      listHeader: "services.templates.description.listHeader",
      listItems: [
        "services.templates.description.listItem1",
        "services.templates.description.listItem2",
        "services.templates.description.listItem3",
        "services.templates.description.listItem4",
        "services.templates.description.listItem5",
        "services.templates.description.listItem6",
      ],
      outro: "services.templates.description.outro",
    },
    imageUrl: "/images/services/vettBOX.webp",
    videoId: "N2IRGq1QdKY",
    serviceImage: "/images/services/vettBOX.webp",
    petImage: "/images/logo/vettBOXPet.webp",
  },
  {
    id: "forum",
    titleKey: "services.forum.title",
    iconKey: "forum",
    descriptionKeys: {
      intro: "services.forum.description.intro",
      tagline: "services.forum.description.tagline",
      benefit: "services.forum.description.benefit",
      listHeader: "services.forum.description.listHeader",
      listItems: [
        "services.forum.description.listItem1",
        "services.forum.description.listItem2",
        "services.forum.description.listItem3",
        "services.forum.description.listItem4",
        "services.forum.description.listItem5",
        "services.forum.description.listItem6",
      ],
      outro: "services.forum.description.outro",
    },
    imageUrl: "/images/services/vetTALK.webp",
    videoId: "iNxMWge3ttQ",
    serviceImage: "/images/services/vetTALK.webp",
    petImage: "/images/logo/WooflyPet(1).webp",
  },
  // VettConsult Video Consultations - Coming Soon
  {
    id: "video-consultations",
    titleKey: "services.video.title",
    navTitle: "VettConsult",
    iconKey: "video",
    descriptionKeys: {
      intro: "services.video.description.intro",
      tagline: "services.video.description.tagline",
      benefit: "services.video.description.benefit",
      listHeader: "services.video.description.listHeader",
      listItems: [
        "services.video.description.listItem1",
        "services.video.description.listItem2",
        "services.video.description.listItem3",
        "services.video.description.listItem4",
      ],
      outro: "services.video.description.outro",
      planNote: "services.video.description.planNote",
    },
    // no imageUrl for the video service (user requested to remove image)
    enabled: false,
    serviceImage: "/images/services/VideoConsultingImage.webp",
    petImage: "/images/logo/VettConsultPet.png",
    categories: [
      {
        id: "company-animals",
        nameKey: "services.video.categories.company",
        svgPlaceholder: "paw",
        imageUrl: "/images/services/CompanionImage.webp",
        subCategories: [
          {
            id: "surgery",
            nameKey: "services.video.subcategories.surgery",
            imageUrl: "/images/subcategories/surgery.jpg",
          },
          {
            id: "neurology",
            nameKey: "services.video.subcategories.neurology",
            imageUrl: "/images/subcategories/neurology.jpg",
          },
          {
            id: "internal-medicine",
            nameKey: "services.video.subcategories.internalMedicine",
            imageUrl: "/images/subcategories/internal-medicine.jpg",
          },
          {
            id: "cardiology",
            nameKey: "services.video.subcategories.cardiology",
            imageUrl: "/images/subcategories/cardiology.jpg",
          },
          {
            id: "emergency-critical",
            nameKey: "services.video.subcategories.emergency",
            imageUrl: "/images/subcategories/emergency-critical.jpg",
          },
          {
            id: "anesthesiology",
            nameKey: "services.video.subcategories.anesthesiology",
            imageUrl: "/images/subcategories/anesthesiology.jpg",
          },
        ],
      },
      {
        id: "exotics",
        nameKey: "services.video.categories.exotics",
        svgPlaceholder: "bird",
        imageUrl: "/images/services/ExoticImage.webp",
        subCategories: [
          {
            id: "exotics-surgery",
            nameKey: "services.video.subcategories.exoticsSurgery",
            imageUrl: "/images/subcategories/exotics-surgery.jpg",
          },
          {
            id: "exotics-medicine",
            nameKey: "services.video.subcategories.exoticsMedicine",
            imageUrl: "/images/subcategories/exotics-medicine.jpg",
          },
          {
            id: "exotics-emergency",
            nameKey: "services.video.subcategories.exoticsEmergency",
            imageUrl: "/images/subcategories/exotics-emergency.jpg",
          },
        ],
      },
      {
        id: "production-animals",
        nameKey: "services.video.categories.production",
        svgPlaceholder: "cow",
        imageUrl: "/images/services/ProductionImage.webp",
        subCategories: [
          {
            id: "prod-medicine",
            nameKey: "services.video.subcategories.prodMedicine",
            imageUrl: "/images/subcategories/prod-medicine.jpg",
          },
          {
            id: "prod-surgery",
            nameKey: "services.video.subcategories.prodSurgery",
            imageUrl: "/images/subcategories/prod-surgery.jpg",
          },
          {
            id: "herd-health",
            nameKey: "services.video.subcategories.herdHealth",
            imageUrl: "/images/subcategories/herd-health.jpg",
          },
        ],
      },
    ],
  },
];
